import { useEffect, useRef, useState } from "react";
import { MessageSquare, X, Sparkles, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import AiChatPanel from "./AiChatPanel";
import { aiCreateSession, aiGetSession, aiListSessions, aiStreamMessage } from "@/lib/ai";

const SUGGESTIONS = [
  "Help me price my reward tiers",
  "How much should I raise?",
  "Draft a thank-you update for my backers",
  "Give me 5 FAQs for my page",
  "Suggest 3 stretch goals for a hardware launch",
];

/**
 * Persistent floating Creator Assistant dock. Renders on any page where
 * a signed-in user is present. Bottom-right FAB → slide-in drawer.
 */
const CreatorAssistantDock = () => {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [booting, setBooting] = useState(false);
  const abortRef = useRef(null);

  const openDock = async () => {
    setOpen(true);
    if (sessionId) return;
    setBooting(true);
    try {
      const { items } = await aiListSessions("assistant");
      setSessions(items);
      if (items.length > 0) {
        await selectSession(items[0].id);
      } else {
        const s = await aiCreateSession({ feature: "assistant", title: "New chat" });
        setSessions([s]);
        setSessionId(s.id);
        const { messages } = await aiGetSession(s.id);
        setMessages(messages);
      }
    } catch (e) {
      toast.error(e.message || "Could not start assistant");
    } finally { setBooting(false); }
  };

  const selectSession = async (id) => {
    setSessionId(id);
    setMessages([]);
    try {
      const { messages } = await aiGetSession(id);
      setMessages(messages);
    } catch (e) { toast.error(e.message || "Could not load chat"); }
  };

  const newChat = async () => {
    try {
      const s = await aiCreateSession({ feature: "assistant", title: "New chat" });
      setSessions((prev) => [s, ...prev]);
      setSessionId(s.id);
      setMessages([]);
    } catch (e) { toast.error(e.message || "Failed"); }
  };

  const sendTurn = async (message) => {
    if (!sessionId) return;
    setMessages((prev) => [...prev, { id: `local_${Date.now()}`, role: "user", content: message }]);
    setStreaming(true);
    setStreamingText("");

    const controller = new AbortController();
    abortRef.current = controller;
    try {
      let assembled = "";
      await aiStreamMessage(sessionId, message, (ev) => {
        if (ev.delta) {
          assembled += ev.delta;
          setStreamingText(assembled);
        } else if (ev.done) {
          setMessages((prev) => [...prev, { id: `local_a_${Date.now()}`, role: "assistant", content: assembled }]);
          setStreamingText("");
        } else if (ev.error) {
          toast.error(ev.error);
        }
      }, controller.signal);
    } catch (e) {
      if (e.name !== "AbortError") toast.error(e.message || "Stream failed");
    } finally {
      setStreaming(false);
    }
  };

  useEffect(() => () => abortRef.current?.abort(), []);

  return (
    <>
      {/* FAB */}
      {!open && (
        <button
          onClick={openDock}
          data-testid="assistant-fab"
          className="fixed bottom-5 right-5 z-40 h-14 w-14 rounded-full btn-saffron grid place-items-center shadow-saffron-lg hover:scale-105 transition-transform"
          aria-label="Open AI Creator Assistant"
        >
          <Sparkles className="h-5 w-5" />
        </button>
      )}

      {/* Drawer */}
      {open && (
        <div className="fixed inset-0 z-50 flex" data-testid="assistant-drawer">
          <button
            aria-label="Dismiss"
            className="flex-1 bg-navy-900/40 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />
          <div className="w-full sm:w-[440px] md:w-[520px] h-full bg-cream-100 shadow-warm-lg flex flex-col border-l border-warm-200">
            {/* header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-warm-200 bg-white/70 backdrop-blur">
              <div className="flex items-center gap-3 min-w-0">
                <div className="grid place-items-center h-9 w-9 rounded-xl bg-navy-800 flex-shrink-0">
                  <Sparkles className="h-4 w-4 text-saffron-400" />
                </div>
                <div className="min-w-0">
                  <div className="font-display font-bold text-navy-800 tracking-tight">Creator Assistant</div>
                  <div className="text-[11px] text-navy-500 truncate">Ask anything about your campaign</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={newChat} data-testid="assistant-new-chat" title="New chat" className="grid place-items-center h-9 w-9 rounded-xl text-navy-600 hover:bg-warm-100"><Plus className="h-4 w-4" /></button>
                <button onClick={() => setOpen(false)} data-testid="assistant-close" className="grid place-items-center h-9 w-9 rounded-xl text-navy-600 hover:bg-warm-100"><X className="h-4 w-4" /></button>
              </div>
            </div>

            {sessions.length > 1 && (
              <div className="px-4 py-2 border-b border-warm-200 bg-cream-100/70 overflow-x-auto whitespace-nowrap flex gap-2">
                {sessions.slice(0, 8).map((s) => (
                  <button
                    key={s.id}
                    onClick={() => selectSession(s.id)}
                    className={`text-xs rounded-full px-3 h-7 flex-shrink-0 font-semibold transition-colors ${
                      s.id === sessionId ? "bg-navy-800 text-white" : "bg-white border border-warm-200 text-navy-600 hover:border-saffron-300"
                    }`}
                  >
                    {(s.title || "Chat").slice(0, 26)}
                  </button>
                ))}
              </div>
            )}

            {booting ? (
              <div className="flex-1 grid place-items-center">
                <Loader2 className="h-6 w-6 animate-spin text-saffron-600" />
              </div>
            ) : (
              <div className="flex-1 min-h-0">
                <AiChatPanel
                  messages={messages}
                  streaming={streaming}
                  streamingText={streamingText}
                  onSend={sendTurn}
                  placeholder="Ask about pricing, story, updates…"
                  suggestions={SUGGESTIONS}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// Backwards-compat named export for feature detection
export { CreatorAssistantDock };
export default CreatorAssistantDock;
